/**
 * What an armed aircraft carries: the schema, and the validator that refuses a nonsensical one.
 *
 * Non-negotiable #8 again — weapons are *data*. Nothing in `sim/` knows a gun exists, and nothing
 * in the weapons code branches on which aircraft it is bolted to. An aeroplane with no `weapons`
 * block is unarmed, and that is the entire mechanism by which the trainer and the aerobat stay
 * unarmed: not a check, not a category test, an absent object.
 *
 * The validator follows `config.ts`'s house style and checks *physical* plausibility rather than
 * only types, because these numbers are authored by hand and the failure mode of a wrong one is
 * subtle. A muzzle velocity in feet per second rather than metres would still fire, still draw
 * tracers and still hit things — just at three times the range, with a flat trajectory, and no
 * error anywhere. So the ranges below are deliberately narrow enough to catch a unit slip and
 * wide enough to admit anything anyone would actually build.
 *
 * `updateFixed` / `updateFrame`: neither. Load-time only.
 */

/**
 * A rotary cannon.
 *
 * Defaults throughout describe an M61A1-class 20 mm gun, because that is what the Vandal carries
 * and because a reader checking these numbers against a reference should be able to find them.
 */
export interface CannonConfig {
  /** Shown on the HUD and in the stores page. */
  name: string;
  /** Cyclic rate [rounds/min]. 6000 for an M61A1. */
  roundsPerMinute: number;
  /** Muzzle velocity [m/s]. 1036 for the M61A1's PGU-28. */
  muzzleVelocityMps: number;
  /** Projectile mass [kg]. */
  roundMassKg: number;
  /** Projectile diameter [m] — the calibre. */
  roundDiameterM: number;
  /** Supersonic drag coefficient, referenced to the frontal area. */
  dragCoefficient: number;
  /**
   * Dispersion cone half-angle [milliradians].
   *
   * The figure gunners quote is usually the 80 %-circle *diameter*, around 8 mils for an M61 in
   * a good installation. This is the half-angle of the cone rounds are sampled from, so it is
   * that figure halved, and 4 mils puts an 80 % group about 2.8 m across at 700 m — which is
   * roughly a fighter's wingspan, and roughly right.
   */
  dispersionMilliradians: number;
  /** Rounds carried. */
  capacity: number;
  /** Every Nth round is a tracer. */
  tracerEvery: number;
  /** Damage one round does to a target's hit points. */
  damagePerRound: number;
  /** How long a round lives before it is recycled [s]. Sets the maximum useful range. */
  lifetimeS: number;
  /** Muzzle position relative to the CG, body axes (x forward, y right, z down) [m]. */
  muzzlePositionM: [number, number, number];
  /**
   * Where the ammunition is stowed, body axes [m]. Defaults to the muzzle when absent.
   *
   * Separate from the muzzle because they are metres apart on a real installation and because the
   * *drum* is what has the mass: five hundred rounds of 20 mm is 51 kg, and where it sits decides
   * how far the centre of gravity trims aft as it is fired away. See `stores.ts`.
   */
  ammunitionPositionM?: [number, number, number];
}

/**
 * An infra-red guided air-to-air missile.
 *
 * Sidewinder-class throughout: an 85 kg airframe with a two-second boost motor, a seeker that has
 * to be pointed roughly at the target to acquire, and a gimbal that can only follow so far off
 * the missile's own nose before the target slides out of the field of view and the round goes
 * ballistic. Each of those is what makes the weapon *defeatable*, which is the point.
 */
export interface MissileConfig {
  name: string;
  /** How many are carried. */
  count: number;
  /** All-up launch mass [kg]. */
  massKg: number;
  /** Propellant burned during the boost [kg]. The airframe coasts at `massKg − propellantMassKg`. */
  propellantMassKg: number;
  /** Motor thrust during the boost [N]. */
  motorThrustN: number;
  /** Boost duration [s]. */
  motorBurnS: number;
  /** Body diameter [m]. */
  diameterM: number;
  /** Drag coefficient, referenced to the frontal area. */
  dragCoefficient: number;
  /**
   * Proportional-navigation constant, `N`.
   *
   * Three to five in every real implementation. Below 3 the missile lags the target and arrives
   * behind it; above 5 it overreacts to seeker noise and wastes energy weaving. Four is the
   * classic choice and is what makes a well-timed break work: the missile commands `N` times the
   * line-of-sight rate, so a late, hard turn demands more lateral acceleration than it has.
   */
  navigationConstant: number;
  /**
   * Lateral acceleration the fins can deliver at {@link manoeuvreReferenceSpeedMps}, sea level [g].
   *
   * A ceiling, not a constant — see {@link manoeuvreReferenceSpeedMps}.
   */
  maxLateralG: number;
  /**
   * The speed, at sea-level density, at which the fins are worth {@link maxLateralG} [m/s].
   *
   * Turning is lift, and lift is `C_L · q̄ · S`. A missile that has burned its motor and spent its
   * speed in a turn cannot pull what it could at launch, and one at 40,000 ft cannot pull what it
   * could at sea level, because in both cases `q̄` has collapsed. Without this a missile is a
   * 30 g threat for the whole 25 seconds of its life regardless of what it has been made to do,
   * which is what turns "evade the missile" into "there is no evading the missile".
   */
  manoeuvreReferenceSpeedMps: number;
  /**
   * Autopilot and airframe response time [s].
   *
   * A missile does not achieve a commanded acceleration instantly: the autopilot has to move the
   * fins, the fins have to change the angle of attack, and the airframe has to respond. A quarter
   * of a second is typical, and it is the single most important number for whether a late break
   * works — during the lag the missile is still flying the *old* solution while the target is
   * already leaving. Model it as zero and no manoeuvre defeats the weapon.
   */
  autopilotLagS: number;
  /** Half-angle of the acquisition cone about the *launch aircraft's* nose [deg]. */
  boresightHalfAngleDeg: number;
  /** Half-angle the seeker head can look off the *missile's* own axis before losing the target [deg]. */
  seekerGimbalHalfAngleDeg: number;
  /** Continuous track needed inside the cone before the tone goes solid [s]. */
  lockTimeS: number;
  /** Below this the missile cannot arm [m]. */
  minRangeM: number;
  /** Beyond this the seeker cannot see a target at all [m]. */
  maxRangeM: number;
  /** Radius at which the proximity fuse functions [m]. */
  proximityFuseM: number;
  /** Self-destruct time [s]. */
  lifetimeS: number;
  /** Damage a detonation does. Comfortably lethal to anything this simulator flies against. */
  warheadDamage: number;
  /** Rail positions relative to the CG, body axes [m]. One per missile carried. */
  stationsM: Array<[number, number, number]>;
  /**
   * Parasite drag area of one round **and its rail**, referenced to nothing [m²].
   *
   * Not the round's frontal area. A 127 mm body presents 0.0127 m², and the installed drag of a
   * wingtip round is a good deal more than that once the rail and the interference at the tip are
   * counted — but a good deal *less* than a pylon-mounted store, because a wingtip launcher part
   * pays for itself as an endplate. 0.010 m² each is the middle of that band: four of them cost
   * about 7 % of the Vandal's zero-lift drag area, which is the right order for wingtip Sidewinders
   * and is what `vandal.gates.test.ts` is re-run against.
   */
  railDragAreaM2?: number;
}

export interface WeaponsConfig {
  cannon?: CannonConfig;
  missile?: MissileConfig;
}

/**
 * Check a weapons block, appending to the caller's issue list.
 *
 * Takes the list rather than throwing so an aircraft config reports every problem it has at once
 * — the same contract the rest of `config.ts`'s validators follow.
 */
export function validateWeapons(
  weapons: WeaponsConfig,
  issues: {
    require(condition: boolean, message: string): void;
    positive(value: unknown, path: string): boolean;
    vector3(value: unknown, path: string): boolean;
  },
): void {
  const cannon = weapons.cannon;
  if (cannon !== undefined) {
    issues.positive(cannon.roundsPerMinute, 'weapons.cannon.roundsPerMinute');
    issues.positive(cannon.muzzleVelocityMps, 'weapons.cannon.muzzleVelocityMps');
    issues.positive(cannon.roundMassKg, 'weapons.cannon.roundMassKg');
    issues.positive(cannon.roundDiameterM, 'weapons.cannon.roundDiameterM');
    issues.positive(cannon.dragCoefficient, 'weapons.cannon.dragCoefficient');
    issues.positive(cannon.capacity, 'weapons.cannon.capacity');
    issues.positive(cannon.lifetimeS, 'weapons.cannon.lifetimeS');
    issues.positive(cannon.damagePerRound, 'weapons.cannon.damagePerRound');
    issues.vector3(cannon.muzzlePositionM, 'weapons.cannon.muzzlePositionM');
    issues.require(
      cannon.tracerEvery >= 1 && Number.isInteger(cannon.tracerEvery),
      'weapons.cannon.tracerEvery must be a whole number of rounds, at least 1',
    );
    issues.require(
      cannon.dispersionMilliradians >= 0 && cannon.dispersionMilliradians < 100,
      `weapons.cannon.dispersionMilliradians (${cannon.dispersionMilliradians}) is outside anything ` +
        'a gun does; it is a half-angle in milliradians, not degrees',
    );
    // A unit slip is the failure this catches: 1036 ft/s entered as 1036 m/s passes every type
    // check ever written and produces a gun with three times the reach it should have.
    issues.require(
      cannon.muzzleVelocityMps > 300 && cannon.muzzleVelocityMps < 2_000,
      `weapons.cannon.muzzleVelocityMps (${cannon.muzzleVelocityMps}) is outside the range of any ` +
        'gun (300–2000 m/s) — check whether that figure is in feet per second',
    );
    issues.require(
      cannon.roundDiameterM > 0.004 && cannon.roundDiameterM < 0.06,
      `weapons.cannon.roundDiameterM (${cannon.roundDiameterM}) is not a calibre in metres`,
    );
    // Rate × capacity is the burst time, and a gun with under two seconds of ammunition is
    // almost certainly a capacity that was meant to be larger.
    const burstS = (cannon.capacity / cannon.roundsPerMinute) * 60;
    issues.require(
      burstS > 2,
      `weapons.cannon holds ${cannon.capacity} rounds at ${cannon.roundsPerMinute} rpm, which is ` +
        `${burstS.toFixed(1)} s of firing — check the capacity`,
    );
  }

  const missile = weapons.missile;
  if (missile !== undefined) {
    issues.positive(missile.massKg, 'weapons.missile.massKg');
    issues.positive(missile.motorThrustN, 'weapons.missile.motorThrustN');
    issues.positive(missile.motorBurnS, 'weapons.missile.motorBurnS');
    issues.positive(missile.diameterM, 'weapons.missile.diameterM');
    issues.positive(missile.dragCoefficient, 'weapons.missile.dragCoefficient');
    issues.positive(missile.maxLateralG, 'weapons.missile.maxLateralG');
    issues.positive(missile.manoeuvreReferenceSpeedMps, 'weapons.missile.manoeuvreReferenceSpeedMps');
    issues.positive(missile.autopilotLagS, 'weapons.missile.autopilotLagS');
    issues.require(
      missile.autopilotLagS < 1,
      `weapons.missile.autopilotLagS (${missile.autopilotLagS} s) is longer than the endgame; the ` +
        'missile would never respond to anything',
    );
    issues.positive(missile.lockTimeS, 'weapons.missile.lockTimeS');
    issues.positive(missile.proximityFuseM, 'weapons.missile.proximityFuseM');
    issues.positive(missile.lifetimeS, 'weapons.missile.lifetimeS');
    issues.positive(missile.warheadDamage, 'weapons.missile.warheadDamage');
    issues.require(
      missile.count >= 1 && Number.isInteger(missile.count),
      'weapons.missile.count must be a whole number, at least 1',
    );
    issues.require(
      Array.isArray(missile.stationsM) && missile.stationsM.length === missile.count,
      `weapons.missile carries ${missile.count} rounds but declares ` +
        `${missile.stationsM?.length ?? 0} station(s) — every missile needs a rail to leave from`,
    );
    if (Array.isArray(missile.stationsM)) {
      for (let i = 0; i < missile.stationsM.length; i++) {
        issues.vector3(missile.stationsM[i], `weapons.missile.stationsM[${i}]`);
      }
    }
    issues.require(
      missile.propellantMassKg > 0 && missile.propellantMassKg < missile.massKg,
      `weapons.missile.propellantMassKg (${missile.propellantMassKg}) must be positive and below ` +
        `the all-up mass (${missile.massKg})`,
    );
    issues.require(
      missile.navigationConstant >= 2 && missile.navigationConstant <= 6,
      `weapons.missile.navigationConstant (${missile.navigationConstant}) is outside 2–6; below 2 ` +
        'the missile trails the target and above 6 it chases seeker noise',
    );
    issues.require(
      missile.minRangeM > 0 && missile.minRangeM < missile.maxRangeM,
      `weapons.missile.minRangeM (${missile.minRangeM}) must be positive and below maxRangeM ` +
        `(${missile.maxRangeM})`,
    );
    issues.require(
      missile.seekerGimbalHalfAngleDeg > missile.boresightHalfAngleDeg,
      `weapons.missile.seekerGimbalHalfAngleDeg (${missile.seekerGimbalHalfAngleDeg}°) must exceed ` +
        `boresightHalfAngleDeg (${missile.boresightHalfAngleDeg}°), or a missile would lose at ` +
        'launch the target it was just locked onto',
    );
    issues.require(
      missile.boresightHalfAngleDeg > 0 && missile.boresightHalfAngleDeg < 90,
      'weapons.missile.boresightHalfAngleDeg must be in (0, 90)',
    );
    issues.require(
      missile.seekerGimbalHalfAngleDeg > 0 && missile.seekerGimbalHalfAngleDeg < 90,
      'weapons.missile.seekerGimbalHalfAngleDeg must be in (0, 90)',
    );
    // The boost has to be worth having: a motor that adds less than about 100 m/s is a rocket
    // that would be outrun by the aeroplane that launched it.
    const deltaV = (missile.motorThrustN * missile.motorBurnS) / missile.massKg;
    issues.require(
      deltaV > 100,
      `weapons.missile's motor adds only ${deltaV.toFixed(0)} m/s over its burn, which would not ` +
        'outrun the launch aircraft — check the thrust and burn time',
    );
  }
}
